import { useContext, useEffect, useMemo, useState } from 'react';
import { useMutation } from '@apollo/client/react';
import clsx from 'clsx';
import * as R from 'ramda';

import TaskHeaderOptions from '@atom/components/common/workOrderDetail/taskOptions/TaskHeaderOptions';
import UserStatusControls from '@atom/components/common/workOrderDetail/userStatusControls/UserStatusControls';
import TaskContext from '@atom/components/workOrderPreview/taskView/TaskContext';
import WorkOrderPreviewContext, {
  WorkOrderActionTypes,
} from '@atom/components/workOrderPreview/WorkOrderPreviewContext';
import { TASK_UPDATE } from '@atom/graph/task';
import { SearchParamType, useNavigation } from '@atom/hooks/useNavigation';
import { usePreferences } from '@atom/hooks/usePreferences';
import { useWorkValidations } from '@atom/hooks/useWorkValidations';
import { Icon, IconButton, Modal, Snackbar, TextField } from '@atom/mui';
import {
  CheckCircleIcon,
  CheckCircleOutlineIcon,
  ErrorOutlineIcon,
  ExpandLessIcon,
} from '@atom/mui/icons';
import colors from '@atom/styles/colors';
import {
  doesNotHaveRolePermissions,
  hasRolePermissions,
  ROLE_SETS,
} from '@atom/utilities/authUtilities';
import { addToSet } from '@atom/utilities/setUtilities';
import { isManagerRequiredForTaskCompletion } from '@atom/utilities/tenantFeatureUtilities';
import { getErrorCode } from '@atom/utilities/validationUtilities';
import {
  getValidationsFromError,
  handleRequiredFieldErrorMessage,
} from '@atom/utilities/workValidationUtilities';

import classes from './taskView.module.css';

const TaskHeader = () => {
  const [, { updateSearchParams }] = useNavigation({
    view: SearchParamType.STRING,
  });
  const {
    collapsedTasks,
    setCollapsedTasks,
    workOrderDetail,
    refetch,
    loading: workOrderLoading,
    dispatch,
  } = useContext(WorkOrderPreviewContext);
  const { task } = useContext(TaskContext);
  const preferences = usePreferences();
  const taskRenamingDisabled = R.pathOr(
    false,
    ['workOrders', 'taskRenamingDisabled'],
    preferences,
  );
  const { workValidations, setWorkValidations } = useWorkValidations();

  const [completeModalOpen, setCompleteModalOpen] = useState<boolean>(false);
  const [renameOpen, setRenameOpen] = useState<boolean>(false);
  const [originalName, setOriginalName] = useState<string>(task?.originalName);

  const [taskUpdate, { loading: taskUpdateLoading }] = useMutation(TASK_UPDATE);

  useEffect(() => {
    setOriginalName(task?.originalName);
  }, [task, task?.originalName]);

  const collapseSection = () => {
    setCollapsedTasks(addToSet(collapsedTasks, task.id));
  };

  const handleCompleteToggle = async () => {
    try {
      const newCompleteValue = !task.isCompleted;
      const completeText = newCompleteValue ? 'Complete' : 'Incomplete';

      Snackbar.info({ message: `Marking ${task.name} ${completeText}...` });

      await taskUpdate({
        variables: {
          input: {
            id: task.id,
            workOrderId: workOrderDetail.id,
            isCompleted: newCompleteValue,
          },
        },
      });

      Snackbar.info({ message: `Marked ${task.name} ${completeText}.` });

      refetch();
    } catch (err) {
      const errorCode = getErrorCode(err);

      const allValidations = getValidationsFromError(err);

      if (errorCode === 422) {
        setWorkValidations({
          workOrderId: workOrderDetail.id,
          taskValidations: allValidations.mappedTaskValidations,
          workValidations: allValidations.workInfoValidations,
        });
      }

      handleRequiredFieldErrorMessage({
        errorCode,
        complete: true,
        allValidations,
        Snackbar,
        updateSearchParams,
        isPreview: true,
      });
    } finally {
      setCompleteModalOpen(false);
    }
  };

  const completeIcon = task.isCompleted
    ? CheckCircleIcon
    : CheckCircleOutlineIcon;
  const completeColor = task.isCompleted
    ? colors.brand.green
    : colors.neutral.silver;
  const tooltipText = task.isCompleted ? 'Mark Incomplete' : 'Mark Complete';

  const handleCompleteClick = () => {
    return task.isCompleted
      ? handleCompleteToggle()
      : setCompleteModalOpen(true);
  };

  const onRename = async () => {
    const res = await taskUpdate({
      variables: {
        input: {
          id: task?.id,
          workOrderId: workOrderDetail?.id,
          name: originalName,
        },
      },
    });

    const name = res?.data?.taskUpdate?.name;
    const newOriginalName = res?.data?.taskUpdate?.originalName;

    dispatch({
      type: WorkOrderActionTypes.UPDATE_TASK_PROPERTY,
      data: {
        taskId: task?.id,
        property: 'name',
        value: name,
      },
    });

    dispatch({
      type: WorkOrderActionTypes.UPDATE_TASK_PROPERTY,
      data: {
        taskId: task?.id,
        property: 'originalName',
        value: newOriginalName,
      },
    });

    setRenameOpen(false);
  };

  // TODO: [AM-7948]: Move to RBAC
  const disableTaskCompleteButton =
    doesNotHaveRolePermissions(
      isManagerRequiredForTaskCompletion
        ? ROLE_SETS.MANAGER
        : ROLE_SETS.INSPECTOR,
    ) || workOrderDetail.isClosed;

  const hasPendingChanges = useMemo(() => {
    return task.assetIds.some(
      id => workOrderDetail.assets[id]?.hasPendingChanges,
    );
  }, [task]);

  const isTaskInvalid = R.has(task.id)(workValidations?.taskValidations);

  const canRenameTask =
    !taskRenamingDisabled &&
    hasRolePermissions(ROLE_SETS.INSPECTOR) &&
    !workOrderDetail.isClosed;

  const taskNameStyles = clsx(
    classes.taskHeaderTitle,
    canRenameTask && classes.taskHeaderTitleHover,
  );

  const onNameClick = canRenameTask ? () => setRenameOpen(true) : () => {};

  return (
    <>
      <div className={classes.headerContainer}>
        <div className={classes.leftHeaderSection}>
          {isTaskInvalid ? (
            <Icon color={colors.brand.red} icon={ErrorOutlineIcon} />
          ) : (
            <div className={classes.changeIndicatorContainer}>
              <IconButton
                size="small"
                onClick={handleCompleteClick}
                disabled={disableTaskCompleteButton}
                tooltip={tooltipText}
                edge="end"
              >
                <Icon color={completeColor} icon={completeIcon} />
              </IconButton>
              {hasPendingChanges && <div className={classes.changeIndicator} />}
            </div>
          )}
          <div className={taskNameStyles} onClick={onNameClick}>
            {task?.name}
          </div>
        </div>
        <div className={classes.rightHeaderSection}>
          <UserStatusControls
            workOrderDetail={workOrderDetail}
            workOrderLoading={workOrderLoading}
            task={task}
            refetch={refetch}
          />
          <TaskHeaderOptions
            workOrderDetail={workOrderDetail}
            task={task}
            refetch={refetch}
            WorkOrderActionTypes={WorkOrderActionTypes}
            dispatch={dispatch}
            dataCyLabel="previewTaskAdditionalActions"
          />
          <IconButton onClick={collapseSection} edge="end">
            <Icon icon={ExpandLessIcon} />
          </IconButton>
        </div>
      </div>
      <Modal
        open={renameOpen}
        cancelButtonText="Cancel"
        confirmButtonText="Save"
        onCancel={() => setRenameOpen(false)}
        onConfirm={onRename}
        disabled={!originalName}
        title="Rename Task"
      >
        <TextField
          label="Task Name *"
          value={originalName}
          name="name"
          onChange={event => setOriginalName(event.target.value)}
        />
      </Modal>
      <Modal
        open={completeModalOpen}
        loading={taskUpdateLoading}
        confirmButtonText="Complete"
        onCancel={() => setCompleteModalOpen(false)}
        onConfirm={handleCompleteToggle}
        title="Complete Task?"
      >
        Not everyone completed the task. This action will complete the task for
        everyone on the task. Are you sure?
      </Modal>
    </>
  );
};

export default TaskHeader;
